/**
 * Interactive Tutorial Configuration
 * Defines hands-on, action-based tutorial steps where users actually interact with the UI
 */

/**
 * Helper function to get date 1 week ago from today
 * Returns date in YYYY-MM-DD format
 */
const getOneWeekAgo = () => {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Helper function to format date for display
 * Converts YYYY-MM-DD to "Month Day, Year" (e.g., "January 15, 2024")
 */
const formatDateForDisplay = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00'); // Add time to avoid timezone issues
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

export const TUTORIAL_IDS = {
  // Research Portal Tutorials (Split into focused lessons)
  SEARCH_QUERY: 'tutorial_search_query',
  ARTICLE_ANNOTATION: 'tutorial_article_annotation',
  CATEGORY_MANAGEMENT: 'tutorial_category_management',
  
  // Original combined tutorial (kept for backwards compatibility)
  RESEARCH_PORTAL: 'research_portal_tutorial',
  
  // AI Assistant Tutorials (Split into focused lessons)
  AI_SELECT_ARTICLES: 'tutorial_ai_select_articles',
  AI_CONVERSATIONS: 'tutorial_ai_conversations',
  AI_SHARING: 'tutorial_ai_sharing',
  
  // Collaboration Tutorials (Split into focused lessons)
  COLLAB_START_CHAT: 'tutorial_collab_start_chat',
  COLLAB_SHARE_CONTENT: 'tutorial_collab_share_content',
  COLLAB_MANAGE: 'tutorial_collab_manage',
  
  // Original combined tutorials (kept for backwards compatibility)
  AI_ASSISTANT: 'ai_assistant_tutorial',
  COLLABORATION: 'collaboration_tutorial'
};

/**
 * Action Types for Interactive Steps
 */
export const ACTION_TYPES = {
  TYPE_TEXT: 'type_text',           // User must type specific text
  SELECT_OPTION: 'select_option',   // User must select from dropdown
  CLICK_BUTTON: 'click_button',     // User must click a button
  SELECT_DATE: 'select_date',       // User must select a date
  TOGGLE_SWITCH: 'toggle_switch',   // User must toggle a switch
  CHECK_CHECKBOX: 'check_checkbox', // User must check a checkbox
  WAIT_FOR_RESULT: 'wait_for_result' // Wait for async operation to complete
};

/**
 * Tutorial 1: Creating Your First Search Query
 * Focused tutorial on search query definition and article fetching (Steps 1-8)
 */
export const searchQueryTutorial = {
  id: TUTORIAL_IDS.SEARCH_QUERY,
  title: 'Creating Your First Search Query',
  description: 'Learn how to define and save research search queries',
  steps: [
    {
      id: 'welcome',
      type: 'info',
      title: 'Welcome to Search Queries! 🔍',
      message: 'This tutorial will guide you through creating your first research search query. You\'ll learn how to define keywords, dates, and AI filters to find relevant articles!',
      instruction: 'Click "Start" to begin',
      targetElement: null,
      allowSkip: true
    },
    {
      id: 'keyword_input',
      type: 'info',
      title: 'Enter Your Research Keyword',
      message: 'Let\'s search for articles about ibuprofen. Type the keyword in the field below.',
      instruction: 'Type: ibuprofen',
      targetElement: '[data-tutorial="keyword-input"]',
      expectedValue: 'ibuprofen',
      validatePartial: true,
      helpText: 'This is the main search term for finding relevant research articles.',
      exampleValue: 'ibuprofen'
    },
    {
      id: 'operator_select',
      type: 'info',
      title: 'Choose Search Operator',
      message: 'The operator determines how keywords are combined.',
      instruction: 'Select "AND" from the dropdown',
      targetElement: '[data-tutorial="operator-select"]',
      expectedValue: 'and',
      helpText: '• AND - Both keywords must appear\n• OR - Either keyword can appear',
      exampleValue: 'and'
    },
    {
      id: 'start_date',
      type: 'info',
      title: 'Set Start Date',
      message: 'Choose when to start fetching articles from. For this tutorial, we\'ll use a recent date (1 week ago) to keep things fast!',
      instruction: `Select: ${formatDateForDisplay(getOneWeekAgo())}`,
      targetElement: '[data-tutorial="start-date"]',
      expectedValue: getOneWeekAgo(),
      helpText: 'For this tutorial, we\'re using a recent date to quickly fetch a small number of articles. In real use, you can select any date range you need!',
      exampleValue: getOneWeekAgo()
    },
    {
      id: 'end_date_explanation',
      type: 'info',
      title: '⚠️ Important: End Date Behavior',
      message: 'Two modes available:<br><strong>Leave EMPTY</strong>:<br>New articles will be automatically fetched every day (perfect for continuous research)<br><strong>Set a date</strong>:<br>Only articles between start and end date will be fetched (one-time historical search)<br>For this tutorial, we\'ll leave it empty for automatic updates!',
      instruction: 'Click "Continue" to proceed',
      targetElement: '[data-tutorial="end-date"]',
      highlight: true,
      emphasize: true
    },
    {
      id: 'domain_select',
      type: 'info',
      title: 'Choose AI Domain Filter (Optional)',
      message: 'Select a domain to have AI automatically classify article relevance.',
      instruction: 'Select "IBU (Ibuprofen)" from the dropdown',
      targetElement: '[data-tutorial="domain-select"]',
      expectedValue: 'IBU',
      helpText: 'Our trained AI models will classify each article as Yes/Maybe/No for relevance.',
      exampleValue: 'IBU',
      allowSkip: true
    },
    {
      id: 'fetch_button',
      type: 'action',
      title: 'Save Your Query! 🚀',
      message: 'Now click the button to save your query and start fetching articles.',
      instruction: 'Click "Save Query"',
      targetElement: '[data-tutorial="fetch-button"]',
      helpText: 'This will save your search criteria and fetch articles. The system will automatically fetch new articles daily!'
    },
    {
      id: 'completion',
      type: 'info',
      title: 'Search Query Created! 🎉',
      message: 'Excellent! You\'ve successfully created your first search query. The system is now fetching articles in the background and will continue to fetch new articles daily.\n\nYou can now explore your articles in the table below!',
      instruction: 'Click "Finish" to complete this tutorial',
      targetElement: null,
      allowSkip: false
    }
  ]
};

/**
 * Tutorial 2: Article Annotation
 * Learn how to highlight text and add comments to articles (Steps 9-17)
 */
export const articleAnnotationTutorial = {
  id: TUTORIAL_IDS.ARTICLE_ANNOTATION,
  title: 'Article Annotation',
  description: 'Learn how to highlight important text and add comments to articles',
  steps: [
    {
      id: 'welcome',
      type: 'info',
      title: 'Welcome to Article Annotation! ✨',
      message: 'This tutorial teaches you how to highlight important text and add comments to your research articles. These annotations help you remember key findings and share insights with your team!',
      instruction: 'Click "Start" to begin',
      targetElement: null,
      allowSkip: true
    },
    {
      id: 'open_article',
      type: 'info',
      title: 'Open an Article 📄',
      message: 'First, let\'s open an article to annotate. Click on any article row in the table below to open it.',
      instruction: 'Click any article row to open it',
      targetElement: '[data-tutorial="article-table"]',
      helpText: 'Opening articles displays the full abstract where you can highlight and comment!',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'highlight_text',
      type: 'info',
      title: 'Select Text to Highlight ✨',
      message: 'Now select any text in the abstract below by clicking and dragging over it. Try selecting a sentence or key phrase!',
      instruction: 'Select some text in the abstract, then click "Continue"',
      targetElement: '[data-tutorial="article-abstract"]',
      helpText: 'You can highlight key findings, important methods, or any text you want to remember.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'click_highlight_button',
      type: 'action',
      title: 'Click the Highlight Button 💡',
      message: 'Great! You\'ve selected text. Now click the colorful Highlight button in the toolbar above to create a highlight.',
      instruction: 'Click the Highlight button',
      targetElement: '[data-tutorial="highlight-button"]',
      helpText: 'The Highlight button appears in the toolbar when you select text!',
      highlight: true
    },
    {
      id: 'choose_highlight_color',
      type: 'info',
      title: 'Choose a Highlight Color 🎨',
      message: 'Pick a color for your highlight from the palette. Different colors can represent different types of information!',
      instruction: 'Click a color to apply it',
      targetElement: '[data-tutorial="highlight-color-picker"]',
      helpText: 'Tip: Use yellow for key findings, pink for methods, blue for conclusions, etc.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'save_highlight',
      type: 'action',
      title: 'Save Your Highlight 💾',
      message: 'Perfect! Now click the Save button to create the highlight with your chosen color.',
      instruction: 'Click "Save"',
      targetElement: '[data-tutorial="highlight-save-button"]',
      highlight: true
    },
    {
      id: 'add_comment_to_text',
      type: 'info',
      title: 'Add Comments to Text 💬',
      message: 'Now let\'s add a comment! Select some text in the abstract again (you can select different text or the same text).',
      instruction: 'Select text in the abstract, then click "Continue"',
      targetElement: '[data-tutorial="article-abstract"]',
      helpText: 'Comments let you add notes, questions, or thoughts directly to specific parts of the text!',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'click_comment_button',
      type: 'action',
      title: 'Click the Comment Button 💬',
      message: 'Excellent! Now click the golden Comment button in the toolbar to add your comment.',
      instruction: 'Click the Comment button',
      targetElement: '[data-tutorial="comment-button"]',
      helpText: 'The Comment button appears alongside the Highlight button!',
      highlight: true
    },
    {
      id: 'comment_text_input',
      type: 'info',
      title: 'Write Your Comment ✍️',
      message: 'Type a short note about the selected text. This could be a question, observation, or insight!',
      instruction: 'Enter your comment, then click "Continue"',
      targetElement: '[data-tutorial="comment-textarea"]',
      helpText: 'Your comments are saved with the article and can be shared with colleagues.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'save_comment',
      type: 'action',
      title: 'Save Your Comment 💾',
      message: 'Great! Now click Save to add your comment to the article.',
      instruction: 'Click "Save"',
      targetElement: '[data-tutorial-comment="comment-save-button"]',
      highlight: true
    },
    {
      id: 'completion',
      type: 'info',
      title: 'Article Annotation Complete! 🎉',
      message: 'Excellent! You now know how to:\n\n✅ Open articles for detailed review\n✅ Highlight important text with colors\n✅ Add comments and notes\n✅ Annotate research findings\n\nYour annotations are saved and can be shared with colleagues through the Collaboration feature!',
      instruction: 'Click "Finish" to complete this tutorial',
      targetElement: null,
      allowSkip: false
    }
  ]
};

/**
 * Tutorial 3: Category Management
 * Learn how to create categories and organize articles (Steps 17-26)
 */
export const categoryManagementTutorial = {
  id: TUTORIAL_IDS.CATEGORY_MANAGEMENT,
  title: 'Category Management',
  description: 'Learn how to create categories and organize your research articles',
  steps: [
    {
      id: 'welcome',
      type: 'info',
      title: 'Welcome to Category Management! 🏷️',
      message: 'This tutorial teaches you how to create custom categories and organize your articles. Categories work across ALL your queries, making it easy to group related articles!',
      instruction: 'Click "Start" to begin',
      targetElement: null,
      allowSkip: true
    },
    {
      id: 'open_categories_panel',
      type: 'info',
      title: 'Open Categories Panel 📂',
      message: 'First, click the Categories button in the top-right to open the categories sidebar.',
      instruction: 'Click "Categories"',
      targetElement: '[data-tutorial="categories-button"]',
      helpText: 'The sidebar will slide in from the right showing all your categories!',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'create_category',
      type: 'action',
      title: 'Create a New Category 🏷️',
      message: 'Now click the "New Category" button to start creating your first category.',
      instruction: 'Click "New Category"',
      targetElement: '[data-tutorial="create-category-button"]',
      helpText: 'Categories help you organize articles by topic, relevance, or any criteria you choose!',
      highlight: true
    },
    {
      id: 'category_name_input',
      type: 'info',
      title: 'Enter Category Name ✏️',
      message: 'Type a name for your category. For example: "Clinical Trials", "Side Effects", or "Mechanisms".',
      instruction: 'Enter a category name, then click "Continue"',
      targetElement: '[data-tutorial="category-name-input"]',
      helpText: 'Choose a descriptive name that helps you identify the category later.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'category_color_picker',
      type: 'info',
      title: 'Choose a Category Color 🎨',
      message: 'Select a color for your category from the palette. Colors help you visually distinguish categories!',
      instruction: 'Click a color, then click "Continue"',
      targetElement: '[data-tutorial="category-color-picker"]',
      helpText: 'Tip: Use different colors for different types of research - green for positive results, red for adverse effects, etc.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'category_save',
      type: 'action',
      title: 'Save Your Category 💾',
      message: 'Perfect! Now click the "Create Category" button to save your new category.',
      instruction: 'Click "Create Category"',
      targetElement: '[data-tutorial="category-save-button"]',
      highlight: true
    },
    {
      id: 'assign_category',
      type: 'action',
      title: 'Assign Category to an Article 📌',
      message: 'Great! Your category is created. Now let\'s assign it to an article. Click any category pill/button in the "Category" column of the article table.',
      instruction: 'Click a Category button in the table',
      targetElement: '[data-tutorial="category-button"]',
      helpText: 'Categories help you group related articles across different queries!',
      highlight: true
    },
    {
      id: 'choose_category_option',
      type: 'info',
      title: 'Select a Category 🏷️',
      message: 'A dropdown will appear. Click on the category you just created to assign it to this article.',
      instruction: 'Click your category from the list',
      targetElement: '[data-tutorial="category-option"]',
      helpText: 'You can assign multiple categories to the same article!',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'save_category_assignment',
      type: 'action',
      title: 'Save Category Assignment 💾',
      message: 'Excellent! Now click Save to assign the category to the article.',
      instruction: 'Click "Save"',
      targetElement: '[data-tutorial="category-assign-save"]',
      highlight: true
    },
    {
      id: 'category_ai_learning',
      type: 'info',
      title: 'AI-Powered Category Suggestions 🤖',
      message: 'Here\'s a powerful feature: Once you\'ve categorized a few articles, our AI learns your categorization patterns and can automatically suggest categories for new articles!',
      instruction: 'Click "Continue" to learn more',
      targetElement: null,
      allowSkip: false
    },
    {
      id: 'completion',
      type: 'info',
      title: 'Category Management Complete! 🎉',
      message: 'Awesome! You now know how to:\n\n✅ Create custom categories with colors\n✅ Open the categories panel\n✅ Assign categories to articles\n✅ Organize research across queries\n✅ Use AI-powered category suggestions\n\nCategories make it easy to find and group related articles, no matter which query they came from!',
      instruction: 'Click "Finish" to complete this tutorial',
      targetElement: null,
      allowSkip: false
    }
  ]
};

/**
 * Welcome & Tutorial Guide
 * Simple first-login guide showing users where to find tutorials
 */
export const researchPortalTutorial = {
  id: TUTORIAL_IDS.RESEARCH_PORTAL,
  title: 'Welcome to Aletheon! 🎉',
  description: 'Quick guide to get you started',
  steps: [
    {
      id: 'welcome',
      type: 'info',
      title: 'Welcome to Aletheon! 🎉',
      message: 'Aletheon helps you search medical literature, use AI assistance, and collaborate with your team.',
      instruction: 'Click "Continue"',
      targetElement: null,
      allowSkip: false
    },
    {
      id: 'complete_profile',
      type: 'info',
      title: 'Complete Your Profile 📝',
      message: '<strong>Important:</strong> Complete your profile to enable collaboration.<br><br><strong>Steps:</strong><br>1. Click profile avatar (top-right)<br>2. Select "Profile"<br>3. Fill: Name, Institution, Role, Research Interests<br>4. Click "Save Changes"',
      instruction: 'Complete profile, then click "Continue"',
      targetElement: '[data-tutorial="profile-avatar"]',
      highlight: true,
      allowSkip: false
    },
    {
      id: 'show_tutorials_button',
      type: 'info',
      title: 'Find Tutorials Here 📚',
      message: 'Access focused tutorials anytime from the <strong>Tutorials</strong> button:<br><br>• Creating Search Queries<br>• Article Annotation<br>• Category Management<br>• AI Assistant<br>• Collaboration',
      instruction: 'Click "Finish" to start',
      targetElement: '[data-tutorial="tutorials-button"]',
      highlight: true,
      allowSkip: false
    }
  ]
};

/**
 * AI Assistant Tutorial 1: Selecting Articles for AI Analysis
 * Learn how to select articles for AI conversations (Steps 1-4)
 */
export const aiSelectArticlesTutorial = {
  id: TUTORIAL_IDS.AI_SELECT_ARTICLES,
  title: 'AI Assistant: Select Articles & Chat',
  description: 'Learn how to select articles and chat with AI about your research',
  steps: [
    {
      id: 'welcome',
      type: 'info',
      title: 'Welcome to AI Article Selection! 📚',
      message: 'This tutorial shows you how to use AI Assistant. First, you\'ll start a new chat, then select articles, and ask questions to get AI-powered insights!',
      instruction: 'Click "Start" to begin',
      targetElement: null,
      allowSkip: true
    },
    {
      id: 'new_chat_button',
      type: 'info',
      title: 'Start a New Chat 🔄',
      message: 'First, click "New Chat" to begin a fresh AI conversation.',
      instruction: 'Click "New Chat"',
      targetElement: '[data-tutorial="new-chat-button"]',
      helpText: 'This creates a new conversation where you\'ll select articles and chat with AI.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'selected_articles_section',
      type: 'info',
      title: 'Selected Articles Panel 📚',
      message: 'This panel shows articles you\'ve selected for AI analysis. The AI will focus on these articles when answering your questions.<br><br><strong>Let\'s try it!</strong> Click "Select All" button below to quickly select the first 4 articles.',
      instruction: 'Click "Select All" to select articles',
      targetElement: '[data-tutorial="select-all-button"]',
      helpText: 'Select All is a quick way to pick multiple articles for AI analysis.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'clear_selection',
      type: 'info',
      title: 'Clear Selection 🔄',
      message: 'Great! The 4 articles now appear in the Selected Articles panel above. Now click "Clear" to remove them.',
      instruction: 'Click "Clear" to deselect all articles',
      targetElement: '[data-tutorial="clear-button"]',
      helpText: 'The Clear button removes all selected articles so you can start fresh.',
      highlight: true,
      allowSkip: true
    },

    {
      id: 'open_query_accordion',
      type: 'info',
      title: 'Open Query to View Articles 📂',
      message: 'Expand a query to see its articles. Click on any query accordion below to open it and select articles by clicking their checkboxes.<br><br><strong>Note:</strong> By default, only articles classified as "Yes" or "Maybe" are shown. Click "Show All" to include "No" articles.',
      instruction: 'Click a query to expand it',
      targetElement: '[data-tutorial="query-accordion"]',
      helpText: 'Each query shows AI-filtered articles. Use the "Show All" toggle to view all articles including those classified as "No".',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'chat_interface',
      type: 'info',
      title: 'AI Chat Interface 💬',
      message: 'This is where you\'ll chat with the AI. Ask questions, request summaries, or get insights about your selected articles!',
      instruction: 'Look at the chat interface',
      targetElement: '[data-tutorial="chat-interface"]',
      helpText: 'The AI analyzes your selected articles and provides intelligent responses.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'ask_first_question',
      type: 'info',
      title: 'Ask Your First Question! 🚀',
      message: 'Type a question in the chat input. Try: "What are the main findings?" or "Summarize the key points"',
      instruction: 'Type a question and press Enter',
      targetElement: '[data-tutorial="chat-input"]',
      helpText: 'The AI will analyze your selected articles and provide comprehensive answers.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'chat_history',
      type: 'info',
      title: 'Chat History 📝',
      message: 'All your AI conversations are saved here. Click any conversation to continue where you left off!',
      instruction: 'Look at the Chat History panel',
      targetElement: '[data-tutorial="chat-history-section"]',
      helpText: 'Your conversations are saved and can be shared with colleagues.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'conversation_actions',
      type: 'info',
      title: 'Manage Conversations ⚙️',
      message: 'Click the menu button (⋮) next to any conversation to rename, share, or delete it.',
      instruction: 'Click a conversation menu button',
      targetElement: '[data-tutorial="conversation-menu-button"]',
      helpText: 'Keep your conversations organized and share insights with your team!',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'shared_conversations',
      type: 'info',
      title: 'Shared Conversations 👥',
      message: 'When colleagues share AI conversations with you, they appear in the Shared Conversations section below Chat History.',
      instruction: 'Look at the Shared Conversations section',
      targetElement: '[data-tutorial="shared-conversations-section"]',
      helpText: 'Shared conversations are read-only but let you see colleague insights.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'completion',
      type: 'info',
      title: 'AI Assistant Complete! 🎉',
      message: 'Excellent! You now know how to:\n\n✅ Select articles for AI analysis\n✅ Start AI conversations\n✅ Ask questions and get insights\n✅ Manage chat history\n✅ View shared conversations',
      instruction: 'Click "Finish" to complete this tutorial',
      targetElement: null,
      allowSkip: false
    }
  ]
};

/**
 * AI Assistant Tutorial 2: AI Conversations & Chat
 * Learn how to chat with AI and manage conversations (Steps 5-9)
 */
export const aiConversationsTutorial = {
  id: TUTORIAL_IDS.AI_CONVERSATIONS,
  title: 'AI Conversations & Chat',
  description: 'Learn how to chat with AI about your research',
  steps: [
    {
      id: 'welcome',
      type: 'info',
      title: 'Welcome to AI Conversations! 💬',
      message: 'This tutorial teaches you how to chat with the AI about your selected articles. The AI can summarize findings, compare studies, and answer research questions!',
      instruction: 'Click "Start" to begin',
      targetElement: null,
      allowSkip: true
    },
    {
      id: 'chat_interface',
      type: 'info',
      title: 'AI Chat Interface 💬',
      message: 'This is where you\'ll chat with the AI. Ask questions, request summaries, or get insights about your selected articles!',
      instruction: 'Look at the chat interface (center panel)',
      targetElement: '[data-tutorial="chat-interface"]',
      helpText: 'The AI analyzes your selected articles and provides intelligent responses.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'ask_first_question',
      type: 'info',
      title: 'Ask Your First Question! 🚀',
      message: 'Type a question in the chat input. Try: "What are the main findings?" or "Summarize the key points"',
      instruction: 'Type a question and press Enter',
      targetElement: '[data-tutorial="chat-input"]',
      helpText: 'The AI will analyze your selected articles and provide comprehensive answers.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'chat_history',
      type: 'info',
      title: 'Chat History 📝',
      message: 'All your AI conversations are automatically saved here. Click any conversation to continue where you left off!',
      instruction: 'Look at the Chat History panel (right side)',
      targetElement: '[data-tutorial="chat-history-section"]',
      helpText: 'Your conversations are saved and can be shared with colleagues.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'new_chat_button',
      type: 'info',
      title: 'Start New Conversations 🔄',
      message: 'Click "New Chat" anytime to start a fresh conversation with different articles or topics.',
      instruction: 'Try clicking "New Chat"',
      targetElement: '[data-tutorial="new-chat-button"]',
      helpText: 'Each new chat starts with a clean slate for different research questions.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'conversation_actions',
      type: 'info',
      title: 'Manage Your Conversations ⚙️',
      message: 'Click the menu button (⋮) next to any conversation to rename, share, or delete it.',
      instruction: 'Click a conversation menu button',
      targetElement: '[data-tutorial="conversation-menu-button"]',
      helpText: 'Keep your conversations organized and share insights with your team!',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'completion',
      type: 'info',
      title: 'AI Conversations Complete! 🎉',
      message: 'Excellent! You now know how to:\n\n✅ Chat with AI about your research\n✅ Ask questions and get insights\n✅ Manage chat history\n✅ Organize conversations\n\nNext, try the "Sharing & Filtering" tutorial to learn collaboration features!',
      instruction: 'Click "Finish" to complete this tutorial',
      targetElement: null,
      allowSkip: false
    }
  ]
};

/**
 * AI Assistant Tutorial 3: Sharing Conversations & Filtering
 * Learn to share AI insights and use AI filtering (Steps 10-13)
 */
export const aiSharingTutorial = {
  id: TUTORIAL_IDS.AI_SHARING,
  title: 'Sharing Conversations & AI Filtering',
  description: 'Learn to share AI insights and filter articles',
  steps: [
    {
      id: 'welcome',
      type: 'info',
      title: 'Welcome to Sharing & Filtering! 📤',
      message: 'This tutorial teaches you how to share AI conversations with colleagues and use AI filtering to focus on relevant articles.',
      instruction: 'Click "Start" to begin',
      targetElement: null,
      allowSkip: true
    },
    {
      id: 'shared_conversations',
      type: 'info',
      title: 'Shared Conversations Panel 👥',
      message: 'This panel shows AI conversations that colleagues have shared with you. Click any to view their AI insights!',
      instruction: 'Look at the Shared Conversations section',
      targetElement: '[data-tutorial="shared-conversations-section"]',
      helpText: 'Shared conversations are read-only but let you see colleague insights.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'share_conversation',
      type: 'info',
      title: 'Share Your AI Insights 📤',
      message: 'Click the menu (⋮) on any conversation and select "Share with Colleagues" to send AI insights to your team.',
      instruction: 'Try the share option on a conversation',
      targetElement: '[data-tutorial="share-conversation-button"]',
      helpText: 'Sharing conversations helps your team learn from each other\'s AI insights!',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'query_filtering',
      type: 'info',
      title: 'AI Filtering Toggle 🎯',
      message: 'Each query has an "AI Filtered" toggle. Click it to show only articles the AI classified as highly relevant!',
      instruction: 'Toggle the AI filter on any query',
      targetElement: '[data-tutorial="ai-filter-toggle"]',
      helpText: 'AI filtering helps you focus on the most relevant articles.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'completion',
      type: 'info',
      title: 'Sharing & Filtering Complete! 🎉',
      message: 'Awesome! You now know how to:\n\n✅ View shared conversations from colleagues\n✅ Share your AI insights with the team\n✅ Use AI filtering to focus on relevant articles\n\nYou\'ve mastered the AI Assistant features!',
      instruction: 'Click "Finish" to complete this tutorial',
      targetElement: null,
      allowSkip: false
    }
  ]
};

/**
 * AI Assistant Interactive Tutorial
 * Comprehensive guide through AI-powered research assistance
 */
export const aiAssistantTutorial = {
  id: TUTORIAL_IDS.AI_ASSISTANT,
  title: 'AI Assistant Tutorial',
  description: 'Learn how to use AI for research assistance!',
  steps: [
    {
      id: 'welcome',
      type: 'info',
      title: 'Welcome to AI Assistant! 🤖',
      message: 'This tutorial will show you how to use AI to analyze your research articles and get intelligent insights. You\'ll learn to select articles, chat with AI, and manage your conversations.',
      instruction: 'Click "Start" to begin',
      targetElement: null,
      allowSkip: true
    },
    {
      id: 'selected_articles_section',
      type: 'info',
      title: 'Selected Articles Panel 📚',
      message: 'This panel shows articles you\'ve selected for AI analysis. You can select up to 4 articles at once for optimal AI performance.',
      instruction: 'Look at the Selected Articles section',
      targetElement: '[data-tutorial="clear-button"]',
      helpText: 'Selected articles become your "knowledge base" for AI conversations. The AI can reference these specific papers when answering your questions.',
      highlight: true
    },
    {
      id: 'select_articles',
      type: 'info',
      title: 'Select Articles for AI Analysis 🎯',
      message: 'Click on any article in the "All Queries" section below to add it to your selection. You can select up to 4 articles.',
      instruction: 'Click on an article checkbox to select it',
      targetElement: '[data-tutorial="article-checkbox"]',
      helpText: 'Selected articles will appear in the left panel and become available for AI analysis.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'select_all_button',
      type: 'info',
      title: 'Quick Selection Tools ⚡',
      message: 'Use these buttons to quickly select or clear all articles. "Select All" will pick the first 4 articles for you.',
      instruction: 'Try the "Select All" button',
      targetElement: '[data-tutorial="select-all-button"]',
      helpText: 'This is useful when you want to analyze all available articles at once.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'chat_interface',
      type: 'info',
      title: 'AI Chat Interface 💬',
      message: 'This is where you\'ll chat with the AI about your selected articles. Ask questions, request summaries, or get insights!',
      instruction: 'Look at the chat interface in the center',
      targetElement: '[data-tutorial="chat-interface"]',
      helpText: 'The AI can analyze your selected articles and provide intelligent responses based on the research content.',
      highlight: true
    },
    {
      id: 'ask_first_question',
      type: 'info',
      title: 'Ask Your First Question! 🚀',
      message: 'Type a question about your selected articles in the chat input below. Try asking something like "What are the main findings?" or "Summarize the key points."',
      instruction: 'Type a question and press Enter or click Send',
      targetElement: '[data-tutorial="chat-input"]',
      helpText: 'The AI will analyze your selected articles and provide a comprehensive answer based on the research content.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'chat_history',
      type: 'info',
      title: 'Chat History 📝',
      message: 'All your AI conversations are saved here. You can return to any previous conversation anytime.',
      instruction: 'Look at the Chat History panel on the right',
      targetElement: '[data-tutorial="chat-history-section"]',
      helpText: 'Your conversations are automatically saved and can be shared with colleagues.',
      highlight: true
    },
    {
      id: 'new_chat_button',
      type: 'info',
      title: 'Start New Conversations 🔄',
      message: 'Click "New Chat" to start a fresh conversation with the AI.',
      instruction: 'Click the "New Chat" button',
      targetElement: '[data-tutorial="new-chat-button"]',
      helpText: 'Starting a new chat clears the current conversation and lets you begin fresh.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'conversation_actions',
      type: 'info',
      title: 'Manage Your Conversations ⚙️',
      message: 'Click the menu button (⋮) next to any conversation to rename, share, or delete it.',
      instruction: 'Click the menu button on a conversation',
      targetElement: '[data-tutorial="conversation-menu-button"]',
      helpText: 'You can organize your conversations by renaming them and share interesting ones with colleagues.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'shared_conversations',
      type: 'info',
      title: 'Shared Conversations 👥',
      message: 'This panel shows conversations that colleagues have shared with you. Click on any shared conversation to view it.',
      instruction: 'Look at the Shared Conversations panel',
      targetElement: '[data-tutorial="shared-conversations-section"]',
      helpText: 'Shared conversations are read-only, but you can see the AI analysis and insights from your colleagues.',
      highlight: true
    },
    {
      id: 'share_conversation',
      type: 'info',
      title: 'Share Your AI Insights 📤',
      message: 'You can share your AI conversations with colleagues. Click the menu on any conversation and select "Share with Colleagues".',
      instruction: 'Try sharing a conversation',
      targetElement: '[data-tutorial="share-conversation-button"]',
      helpText: 'Sharing conversations helps your team collaborate and learn from each other\'s AI insights.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'query_filtering',
      type: 'info',
      title: 'Filter Articles by AI Predictions 🎯',
      message: 'Each query has an "AI Filtered" toggle. This shows only articles the AI classified as highly relevant.',
      instruction: 'Try toggling the "AI Filtered" button on a query',
      targetElement: '[data-tutorial="ai-filter-toggle"]',
      helpText: 'AI filtering helps you focus on the most relevant articles based on your research domain.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'completion',
      type: 'info',
      title: 'Tutorial Complete! 🎉',
      message: 'Congratulations! You now know how define the search queries and  \n\nStart exploring your research with AI assistance!',
      instruction: 'Click "Finish" to start using AI!',
      targetElement: null,
      allowSkip: false
    }
  ]
};

/**
 * Collaboration Tutorial 1: Starting Conversations
 * Learn how to start chatting with colleagues (Steps 1-7)
 */
export const collabStartChatTutorial = {
  id: TUTORIAL_IDS.COLLAB_START_CHAT,
  title: 'Collaboration: Chat & Share',
  description: 'Learn how to chat with colleagues and share research content',
  steps: [
    {
      id: 'welcome',
      type: 'info',
      title: 'Welcome to Team Collaboration! 👥',
      message: 'This tutorial shows you how to collaborate with your research team. You\'ll learn to start conversations, share articles, and manage your chat history!',
      instruction: 'Click "Start" to begin',
      targetElement: null,
      allowSkip: true
    },
    {
      id: 'conversation_history',
      type: 'info',
      title: 'Conversation History 📚',
      message: 'This panel shows all your conversations with colleagues. Currently empty, but we\'ll create your first one!',
      instruction: 'Look at the Conversation History panel',
      targetElement: '[data-tutorial="conversation-history-section"]',
      helpText: 'Track all your team discussions in one place.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'new_chat_button',
      type: 'action',
      title: 'Start a New Chat 🚀',
      message: 'Click "New Chat" to start a conversation with a colleague.',
      instruction: 'Click the "New Chat" button',
      targetElement: '[data-tutorial="new-chat-button"]',
      helpText: 'This opens the colleague search dialog.',
      highlight: true
    },
    {
      id: 'search_users',
      type: 'info',
      title: 'Search for Colleagues 🔍',
      message: 'Type a colleague\'s name, email, or institution in the search box to find them.',
      instruction: 'Type in the search box',
      targetElement: '[data-tutorial="user-search-input"]',
      helpText: 'Search helps you quickly find specific team members.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'select_user',
      type: 'action',
      title: 'Select a Colleague 💬',
      message: 'Click on any colleague from the search results to start a conversation with them.',
      instruction: 'Click on a user from the results',
      targetElement: '[data-tutorial="user-search-result"]',
      helpText: 'This opens the chat interface for that colleague.',
      highlight: true
    },
    {
      id: 'chat_interface',
      type: 'info',
      title: 'Chat Interface 💬',
      message: 'Great! You\'re now in a conversation. This is where you\'ll send messages and share content with your colleague.',
      instruction: 'Look at the chat interface',
      targetElement: '[data-tutorial="chat-interface"]',
      helpText: 'Real-time communication with your research team!',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'send_message',
      type: 'info',
      title: 'Send Your First Message! 👋',
      message: 'Type a message in the input field and press Enter or click Send to send it.',
      instruction: 'Type and send a message',
      targetElement: '[data-tutorial="message-input"]',
      helpText: 'Start the conversation with a greeting or research question!',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'share_article',
      type: 'action',
      title: 'Open Sharing Menu 📄',
      message: 'Click the attachment button (+) in the message input to open the sharing menu.',
      instruction: 'Click the + button',
      targetElement: '[data-tutorial="attachment-button"]',
      helpText: 'You can share articles or AI conversations with colleagues.',
      highlight: true
    },
    {
      id: 'select_share_type',
      type: 'info',
      title: 'Share Articles 📄',
      message: 'Click "Share Article" to share research articles with your colleague.',
      instruction: 'Click "Share Article"',
      targetElement: '[data-tutorial="share-article"]',
      helpText: 'This opens the article selection dialog.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'select_article',
      type: 'info',
      title: 'Select Articles to Share 📂',
      message: 'Your articles are organized by query. Expand a query accordion to see its articles, then click on any article to select it for sharing.',
      instruction: 'Expand a query and select an article',
      targetElement: '[data-tutorial="article-list-container"]',
      helpText: 'You can select multiple articles by clicking on them.',
      highlight: true,
      emphasize: true,
      allowSkip: true
    },
    {
      id: 'click_share_button',
      type: 'action',
      title: 'Send the Content! 🚀',
      message: 'Click the "Share" button to send the selected articles to your colleague.',
      instruction: 'Click "Share"',
      targetElement: '[data-tutorial="share-submit-button"]',
      helpText: 'The articles will appear in your chat as interactive cards!',
      highlight: true,
      emphasize: true
    },
    {
      id: 'view_shared_content',
      type: 'action',
      title: 'View Shared Content 📄',
      message: 'Excellent! The article appears in your chat. Click on it to view the full details!',
      instruction: 'Click the shared article card',
      targetElement: '[data-tutorial="shared-content"]',
      helpText: 'Shared articles show full details when clicked.',
      highlight: true,
      emphasize: true
    },
    {
      id: 'conversation_search',
      type: 'info',
      title: 'Search Within Conversations 🔍',
      message: 'Use the search bar to find specific messages or shared content within a conversation.',
      instruction: 'Try searching in the conversation',
      targetElement: '[data-tutorial="conversation-search-bar"]',
      helpText: 'Quickly find information in long conversations.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'conversation_menu',
      type: 'action',
      title: 'Manage Conversations ⚙️',
      message: 'Click the menu button (⋮) next to any conversation to delete or manage it.',
      instruction: 'Click the menu button',
      targetElement: '[data-tutorial="conversation-menu-button"]',
      helpText: 'Keep your collaboration history organized!',
      highlight: true
    },
    {
      id: 'completion',
      type: 'info',
      title: 'Collaboration Complete! 🎉',
      message: 'Amazing! You\'ve mastered collaboration! You now know how to:\n\n✅ Start conversations with colleagues\n✅ Share articles and AI insights\n✅ Search within conversations\n✅ Manage your chat history',
      instruction: 'Click "Finish" to complete this tutorial',
      targetElement: null,
      allowSkip: false
    }
  ]
};

/**
 * Collaboration Tutorial 2: Sharing Articles & Content
 * Learn how to share research with colleagues (Steps 8-12)
 */
export const collabShareContentTutorial = {
  id: TUTORIAL_IDS.COLLAB_SHARE_CONTENT,
  title: 'Sharing Articles & Research Content',
  description: 'Learn how to share articles and AI insights with your team',
  steps: [
    {
      id: 'welcome',
      type: 'info',
      title: 'Welcome to Content Sharing! 📤',
      message: 'This tutorial teaches you how to share research articles and AI conversations with your colleagues!',
      instruction: 'Click "Start" to begin',
      targetElement: null,
      allowSkip: true
    },
    {
      id: 'share_article',
      type: 'action',
      title: 'Open Sharing Menu 📄',
      message: 'Click the attachment button (+) in the message input to open the sharing menu.',
      instruction: 'Click the + button',
      targetElement: '[data-tutorial="attachment-button"]',
      helpText: 'You can share articles or AI conversations with colleagues.',
      highlight: true
    },
    {
      id: 'select_share_type',
      type: 'info',
      title: 'Share Articles 📄',
      message: 'Click "Share Article" to share research articles with your colleague.',
      instruction: 'Click "Share Article"',
      targetElement: '[data-tutorial="share-article"]',
      helpText: 'This opens the article selection dialog.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'select_article',
      type: 'info',
      title: 'Select Articles to Share 📂',
      message: 'Your articles are organized by query. Expand a query accordion to see its articles, then click on any article to select it for sharing.',
      instruction: 'Expand a query and select an article',
      targetElement: '[data-tutorial="article-list-container"]',
      helpText: 'You can select multiple articles by clicking on them.',
      highlight: true,
      emphasize: true,
      allowSkip: true
    },
    {
      id: 'click_share_button',
      type: 'action',
      title: 'Send the Content! 🚀',
      message: 'Click the "Share" button to send the selected articles to your colleague.',
      instruction: 'Click "Share"',
      targetElement: '[data-tutorial="share-submit-button"]',
      helpText: 'The articles will appear in your chat as interactive cards!',
      highlight: true,
      emphasize: true
    },
    {
      id: 'view_shared_content',
      type: 'action',
      title: 'View Shared Content 📄',
      message: 'Excellent! The article appears in your chat. Click on it to view the full details including abstract and annotations!',
      instruction: 'Click the shared article card',
      targetElement: '[data-tutorial="shared-content"]',
      helpText: 'Shared articles show full details when clicked.',
      highlight: true,
      emphasize: true
    },
    {
      id: 'completion',
      type: 'info',
      title: 'Content Sharing Complete! 🎉',
      message: 'Great job! You now know how to:\n\n✅ Open the sharing menu\n✅ Select articles to share\n✅ Send content to colleagues\n✅ View shared content\n\nNext, try "Managing Conversations" to learn organizational features!',
      instruction: 'Click "Finish" to complete this tutorial',
      targetElement: null,
      allowSkip: false
    }
  ]
};

/**
 * Collaboration Tutorial 3: Managing Conversations
 * Learn to search and manage conversations (Steps 13-15)
 */
export const collabManageTutorial = {
  id: TUTORIAL_IDS.COLLAB_MANAGE,
  title: 'Managing Conversations',
  description: 'Learn to search and organize your team conversations',
  steps: [
    {
      id: 'welcome',
      type: 'info',
      title: 'Welcome to Conversation Management! ⚙️',
      message: 'This tutorial teaches you how to search within conversations and manage your chat history!',
      instruction: 'Click "Start" to begin',
      targetElement: null,
      allowSkip: true
    },
    {
      id: 'conversation_search',
      type: 'info',
      title: 'Search Within Conversations 🔍',
      message: 'Use the search bar to find specific messages or shared content within a conversation. Try searching for a keyword!',
      instruction: 'Try searching in the conversation',
      targetElement: '[data-tutorial="conversation-search-bar"]',
      helpText: 'Quickly find information in long conversations.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'conversation_menu',
      type: 'action',
      title: 'Conversation Actions ⚙️',
      message: 'Click the menu button (⋮) next to any conversation to delete or manage it.',
      instruction: 'Click the menu button',
      targetElement: '[data-tutorial="conversation-menu-button"]',
      helpText: 'Keep your collaboration history organized!',
      highlight: true
    },
    {
      id: 'completion',
      type: 'info',
      title: 'Collaboration Complete! 🎉',
      message: 'Amazing! You\'ve mastered collaboration! You now know how to:\n\n✅ Start conversations with colleagues\n✅ Share articles and AI insights\n✅ Search within conversations\n✅ Manage your chat history\n\nYou\'re ready to collaborate with your research team!',
      instruction: 'Click "Finish" to complete this tutorial',
      targetElement: null,
      allowSkip: false
    }
  ]
};

/**
 * Collaboration Interactive Tutorial
 * Comprehensive guide through team collaboration features
 */
export const collaborationTutorial = {
  id: TUTORIAL_IDS.COLLABORATION,
  title: 'Collaboration Tutorial',
  description: 'Learn how to collaborate with your team!',
  steps: [
    {
      id: 'welcome',
      type: 'info',
      title: 'Welcome to Collaboration! 👥',
      message: 'This tutorial will show you how to collaborate with your research team! We\'ll create a conversation with "Aletheon Bot" to demonstrate all the features. You\'ll learn to start conversations, share content, and manage team interactions.',
      instruction: 'Click "Start" to begin your Bot conversation tutorial',
      targetElement: null,
      allowSkip: true
    },
    {
      id: 'conversation_history',
      type: 'info',
      title: 'Conversation History 📚',
      message: 'This panel shows all your previous conversations with colleagues. Currently, it\'s empty, but we\'ll create your first conversation with "Aletheon Bot" (a tutorial assistant) to demonstrate the features!',
      instruction: 'Click "Continue" to proceed',
      targetElement: '[data-tutorial="conversation-history-section"]',
      helpText: 'Your conversation history will help you keep track of ongoing discussions with different team members.',
      highlight: true
    },
    {
      id: 'new_chat_button',
      type: 'action',
      title: 'Start New Conversations 🚀',
      message: 'Click "New Chat" to start a conversation. We\'ll search for "Aletheon Bot" to create your first collaboration conversation!',
      instruction: 'Click the "New Chat" button to start',
      targetElement: '[data-tutorial="new-chat-button"]',
      helpText: 'Starting new chats helps you connect with different team members for various research discussions.',
      highlight: true
    },
    {
      id: 'search_users',
      type: 'info',
      title: 'Search for Aletheon Bot 🔍',
      message: 'Type "Aletheon" in the search box to find the tutorial Bot user. The Bot should appear in the search results below.',
      instruction: 'Type "Aletheon" in the search box',
      targetElement: '[data-tutorial="user-search-input"]',
      helpText: 'Searching helps you quickly locate specific users.',
      highlight: true,
      allowSkip: true,
      expectedValue: 'Aletheon',
      validatePartial: true
    },
    {
      id: 'select_user',
      type: 'action',
      title: 'Select Aletheon Bot 💬',
      message: 'Perfect! You should now see "Aletheon Bot" in the search results below. Click on it to start your conversation. The Bot is created specifically for tutorial purposes.',
      instruction: 'Click on "Aletheon Bot" from the search results',
      targetElement: '[data-tutorial="user-search-result"]',
      helpText: 'The Aletheon Bot was automatically created when you signed up and is designed to help you learn the collaboration features.',
      highlight: true
    },
    {
      id: 'chat_interface',
      type: 'info',
      title: 'Chat Interface 💬',
      message: 'Great! Now you\'re in a conversation with Aletheon Bot. This is the main chat area where you can send messages and share content.',
      instruction: 'Look at the chat interface on the right',
      targetElement: '[data-tutorial="chat-interface"]',
      helpText: 'The chat interface allows real-time communication and content sharing with your team.',
      highlight: true
    },
    {
      id: 'send_message',
      type: 'info',
      title: 'Send a Greeting! 👋',
      message: 'Type "Hi!" or "Hello!" in the message field and press Enter to send your first message to Aletheon Bot. This will start the conversation!',
      instruction: 'Type "Hi!" and send it to the Bot',
      targetElement: '[data-tutorial="message-input"]',
      helpText: 'Sending a greeting starts your conversation and demonstrates real-time communication.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'share_article',
      type: 'action',
      title: 'Share Articles with Bot 📄',
      message: 'Now let\'s learn how to share research articles! Click the attachment button (+) to open the sharing menu, then select "Share Article" from the options.',
      instruction: 'Click (+) then select "Share Article"',
      targetElement: '[data-tutorial="attachment-button"]',
      helpText: 'You can share research articles or AI conversations with your colleagues. For now, we\'ll focus on sharing articles. AI conversations can be shared once you have them!',
      highlight: true
    },
    {
      id: 'select_article',
      type: 'info',
      title: 'Select Articles to Share 📂',
      message: 'Your articles are organized by research query in accordion panels. Click on any query accordion to expand it, then click on any article to select it for sharing.',
      instruction: 'Expand a query and click on an article to select it',
      targetElement: '[data-tutorial="article-list-container"]',
      helpText: 'You can select multiple articles by clicking on them. Click again to deselect.',
      highlight: true,
      emphasize: true,
      waitForAction: true
    },
    {
      id: 'click_share_button',
      type: 'action',
      title: 'Complete the Share! 🚀',
      message: 'Perfect! You\'ve selected an article. Now click the "Share" button at the bottom of the dialog to send it to Aletheon Bot. The article will appear in your chat conversation!',
      instruction: 'Click the "Share" button',
      targetElement: '[data-tutorial="share-submit-button"]',
      helpText: 'The article will be sent to the Bot and appear in your chat as an interactive card. You can also share AI conversations this way once you have them!',
      highlight: true,
      emphasize: true
    },
    {
      id: 'view_shared_content',
      type: 'action',
      title: 'View Shared Article Card 📄',
      message: 'Excellent! The article has been shared and now appears in your chat as an interactive card. You can see the article title, journal, and year. Click on the article card to view its full details!',
      instruction: 'Click on the shared article card to view details',
      targetElement: '[data-tutorial="shared-content"]',
      helpText: 'Shared articles appear as clickable cards. Click on them to see the full abstract, authors, and any shared annotations.',
      highlight: true,
      emphasize: true
    },
    {
      id: 'article_detail_view',
      type: 'info',
      title: 'Article Details Window 📖',
      message: 'Great! The article details window has opened in the center of your screen, showing the full abstract, authors, journal information, and any shared annotations. This is how your colleagues will see the articles you share with them.',
      instruction: 'Review the article details, then click "Continue"',
      targetElement: '[data-tutorial="article-detail-window"]',
      helpText: 'You can read the full article details here. The window is resizable - drag the right edge to resize it. Click outside or press ESC to close.',
      highlight: true,
      emphasize: true
    },
    {
      id: 'conversation_search',
      type: 'info',
      title: 'Search Within Your Bot Conversation 🔍',
      message: 'Use the search bar above the chat to find specific messages or shared content within your conversation with Aletheon Bot. Try searching for "Hi" or "share".',
      instruction: 'Try searching within the Bot conversation',
      targetElement: '[data-tutorial="conversation-search-bar"]',
      helpText: 'Searching within conversations helps you quickly find specific information from long discussions.',
      highlight: true,
      allowSkip: true
    },
    {
      id: 'conversation_menu',
      type: 'action',
      title: 'Manage Your Bot Conversation ⚙️',
      message: 'Click the menu button (⋮) next to your Aletheon Bot conversation, then click "Delete Conversation" to clean up this tutorial conversation.',
      instruction: 'Click menu (⋮) then "Delete Conversation"',
      targetElement: '[data-tutorial="conversation-menu-button"]',
      helpText: 'Managing conversations helps you keep your collaboration history organized. Don\'t worry - deleting the Bot conversation is part of the tutorial!',
      highlight: true
    },
    {
      id: 'completion',
      type: 'info',
      title: 'Tutorial Complete! 🎉',
      message: 'Excellent! You\'ve successfully completed the collaboration tutorial by:\n\n. You\'re now ready to collaborate with your real research team!',
      instruction: 'Click "Finish" to start collaborating with your team!',
      targetElement: null,
      allowSkip: false
    }
  ]
};

/**
 * Helper function to get tutorial by ID
 */
export const getTutorialConfig = (tutorialId) => {
  switch (tutorialId) {
    case TUTORIAL_IDS.SEARCH_QUERY:
      return searchQueryTutorial;
    case TUTORIAL_IDS.ARTICLE_ANNOTATION:
      return articleAnnotationTutorial;
    case TUTORIAL_IDS.CATEGORY_MANAGEMENT:
      return categoryManagementTutorial;
    case TUTORIAL_IDS.RESEARCH_PORTAL:
      return researchPortalTutorial;
    case TUTORIAL_IDS.AI_SELECT_ARTICLES:
      return aiSelectArticlesTutorial;
    case TUTORIAL_IDS.AI_CONVERSATIONS:
      return aiConversationsTutorial;
    case TUTORIAL_IDS.AI_SHARING:
      return aiSharingTutorial;
    case TUTORIAL_IDS.COLLAB_START_CHAT:
      return collabStartChatTutorial;
    case TUTORIAL_IDS.COLLAB_SHARE_CONTENT:
      return collabShareContentTutorial;
    case TUTORIAL_IDS.COLLAB_MANAGE:
      return collabManageTutorial;
    case TUTORIAL_IDS.AI_ASSISTANT:
      return aiAssistantTutorial;
    case TUTORIAL_IDS.COLLABORATION:
      return collaborationTutorial;
    default:
      return null;
  }
};

/**
 * Get all available tutorials
 */
export const getAllTutorials = () => [
  searchQueryTutorial,
  articleAnnotationTutorial,
  categoryManagementTutorial,
  researchPortalTutorial,
  aiSelectArticlesTutorial,
  aiConversationsTutorial,
  aiSharingTutorial,
  collabStartChatTutorial,
  collabShareContentTutorial,
  collabManageTutorial,
  aiAssistantTutorial,
  collaborationTutorial
];

